/**
 * Consistent per-participant color palette.
 * Index 0 is the "default" (gray bubble + indigo name) — used for the first
 * partner in a conversation (1-on-1 chats always use this).
 * From index 1 onward, subtle tints are added for visual distinction.
 * No rose/pink shades. No green shades (reserved for patient name display).
 * All Tailwind class names are written as full strings so the JIT scanner
 * can include them without safelisting.
 */
const PARTICIPANT_COLORS = [
  // 0: pure gray bubble + indigo name — the "default" single-partner look
  {
    bubbleBg: 'bg-gray-50',    bubbleText: 'text-gray-900', nameColor: 'text-indigo-600',
    avatarBg: 'bg-indigo-100', avatarText: 'text-indigo-700',
    pillBg:   'bg-indigo-100', pillText:   'text-indigo-700',
  },
  // 1: very slightly violet-tinted gray bubble
  {
    bubbleBg: 'bg-violet-50',  bubbleText: 'text-gray-900', nameColor: 'text-violet-600',
    avatarBg: 'bg-violet-100', avatarText: 'text-violet-700',
    pillBg:   'bg-violet-100', pillText:   'text-violet-700',
  },
  // 2: very slightly amber-tinted gray bubble (no pink/rose)
  {
    bubbleBg: 'bg-amber-50',   bubbleText: 'text-gray-900', nameColor: 'text-amber-700',
    avatarBg: 'bg-amber-100',  avatarText: 'text-amber-700',
    pillBg:   'bg-amber-100',  pillText:   'text-amber-700',
  },
  // 3: very slightly sky-tinted gray bubble
  {
    bubbleBg: 'bg-sky-50',     bubbleText: 'text-gray-900', nameColor: 'text-sky-600',
    avatarBg: 'bg-sky-100',    avatarText: 'text-sky-700',
    pillBg:   'bg-sky-100',    pillText:   'text-sky-700',
  },
  // 4: very slightly fuchsia-tinted gray bubble
  {
    bubbleBg: 'bg-fuchsia-50', bubbleText: 'text-gray-900', nameColor: 'text-fuchsia-600',
    avatarBg: 'bg-fuchsia-100',avatarText: 'text-fuchsia-700',
    pillBg:   'bg-fuchsia-100',pillText:   'text-fuchsia-700',
  },
  // 5: very slightly teal-tinted gray bubble
  {
    bubbleBg: 'bg-teal-50',    bubbleText: 'text-gray-900', nameColor: 'text-teal-600',
    avatarBg: 'bg-teal-100',   avatarText: 'text-teal-700',
    pillBg:   'bg-teal-100',   pillText:   'text-teal-700',
  },
] as const;

export type ParticipantColor = typeof PARTICIPANT_COLORS[number];

/** Returns a color by position index (for position-based coloring within a conversation). */
export function getParticipantColorByIndex(index: number): ParticipantColor {
  return PARTICIPANT_COLORS[index % PARTICIPANT_COLORS.length];
}

/** Returns a color by name ('indigo', 'violet', 'amber', 'sky', 'fuchsia', 'teal'). */
const COLOR_NAME_MAP: Record<string, ParticipantColor> = {
  indigo: PARTICIPANT_COLORS[0],
  violet: PARTICIPANT_COLORS[1],
  amber: PARTICIPANT_COLORS[2],
  sky: PARTICIPANT_COLORS[3],
  fuchsia: PARTICIPANT_COLORS[4],
  teal: PARTICIPANT_COLORS[5],
};

export const COLOR_NAMES = ['indigo', 'violet', 'amber', 'sky', 'fuchsia', 'teal'] as const;
export type ColorName = typeof COLOR_NAMES[number];

export function getParticipantColorByName(name: string): ParticipantColor {
  return COLOR_NAME_MAP[name] ?? PARTICIPANT_COLORS[0];
}

/** Fallback: hash-based coloring when no position info is available. */
export function getParticipantColor(userId: string): ParticipantColor {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return PARTICIPANT_COLORS[Math.abs(hash) % PARTICIPANT_COLORS.length];
}
